import { type MouseEvent, type ReactNode } from "react";
import { ArrowLeft } from "lucide-react";

import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { cn } from "@/lib/utils";
import { AdminButton } from "@/shared/design-system/admin/AdminButton";
import { InternalLink } from "@/shared/ui/internal-link";

type AdminPageHeaderBack = {
  label: string;
  href?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** Structured element id wiring click analytics for the back affordance. */
  analyticsElementId?: AnalyticsElementId;
  ariaLabel?: string;
};

type AdminPageHeaderProps = {
  title: ReactNode;
  /**
   * A decorative visual rendered immediately before the title — the avatar or glyph of the entity
   * the page shows. The mirror of `titleTrailing`: it sits outside the `<h1>` and is hidden from
   * assistive technology, so the heading holds only the title.
   */
  titleLeading?: ReactNode;
  /** Controls rendered immediately after the title, such as a scope toggle. */
  titleTrailing?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  back?: AdminPageHeaderBack;
  eyebrow?: ReactNode;
  titleSize?: "lg" | "md" | "sm";
  /**
   * Cross-axis alignment of the header block. "center" is for the hero pages
   * (`AdminHeroPage`): centring needs a class on the title column, not just
   * `text-center` on the header, because the title is a flex item inside a row
   * as wide as the widest sibling — a description longer than the title would
   * otherwise leave the title flush left inside a centred column.
   */
  align?: "start" | "center";
  className?: string;
};

const titleSizeClass = {
  lg: "text-2xl font-semibold text-(--color-text-primary)",
  md: "text-xl font-semibold text-(--color-text-primary)",
  sm: "text-lg font-semibold text-(--color-text-primary)",
} as const satisfies Record<NonNullable<AdminPageHeaderProps["titleSize"]>, string>;

export const AdminPageHeader = ({
  title,
  titleLeading,
  titleTrailing,
  description,
  actions,
  back,
  eyebrow,
  titleSize = "lg",
  align = "start",
  className,
}: AdminPageHeaderProps) => {
  const centered = align === "center";
  const heading = <h1 className={titleSizeClass[titleSize]}>{title}</h1>;

  return (
    <header className={cn("flex flex-col gap-4", centered && "items-center text-center", className)}>
      {back ? <AdminPageHeaderBackLink {...back} /> : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className={cn("flex min-w-0 flex-col gap-2", centered && "items-center")}>
          {eyebrow ? <div className="flex flex-wrap items-center gap-2">{eyebrow}</div> : null}
          <div className="flex flex-wrap items-center gap-4">
            {titleLeading ? (
              // Grouped with the heading so a wrapping row never parts the visual from the title.
              <div className="flex min-w-0 items-center gap-3">
                <div aria-hidden className="flex shrink-0">
                  {titleLeading}
                </div>
                {heading}
              </div>
            ) : (
              heading
            )}
            {titleTrailing ? <div className="flex items-center gap-2">{titleTrailing}</div> : null}
          </div>
          {/* A long unbroken token (an email, a URL) wraps instead of running past a phone's gutter. */}
          {description ? (
            <div className="text-sm wrap-break-word text-(--color-text-secondary)">{description}</div>
          ) : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2 sm:flex-none">{actions}</div> : null}
      </div>
    </header>
  );
};

export const AdminPageHeaderBackLink = ({
  label,
  href,
  onClick,
  analyticsElementId,
  ariaLabel,
}: AdminPageHeaderBack) => {
  const content = (
    <>
      <ArrowLeft className="size-4 transition group-hover:-translate-x-0.5" aria-hidden />
      {label}
    </>
  );

  const sharedClassName = "group gap-2 text-sm text-(--color-text-secondary)";

  if (href) {
    return (
      <div className="self-start">
        <AdminButton variant="text" size="sm" className={sharedClassName} asChild>
          <InternalLink
            href={href}
            aria-label={ariaLabel ?? label}
            analyticsElementId={analyticsElementId}
            scrollToTop={false}
            onClick={onClick}
          >
            {content}
          </InternalLink>
        </AdminButton>
      </div>
    );
  }

  return (
    <div className="self-start">
      <AdminButton
        type="button"
        variant="text"
        size="sm"
        className={sharedClassName}
        aria-label={ariaLabel ?? label}
        analyticsElementId={analyticsElementId}
        onClick={onClick}
      >
        {content}
      </AdminButton>
    </div>
  );
};
